import { t } from "../../i18n";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Map as LMap, CircleMarker } from "leaflet";
import { fmt } from "@tasduvar/engine";
import { Dialog } from "../../ui/Dialog";
import { parseTR } from "../../lib/number";
import { lookupTdth, type DdLevel, type TdthValues } from "./tdth";

export const AFAD_TDTH_URL = "https://tdth.afad.gov.tr";
export interface PickedLocation extends TdthValues { lat: number; lon: number }

const coord = (v: number): string => v.toFixed(6).replace(".", ",");

/**
 * "Konumdan al" (Cem 2026-09-30): haritada nokta seç → SS/S1 AFAD TDTH ızgarasından (yaklaşık).
 * Harita altlığı OpenStreetMap (tile.openstreetmap.org); Leaflet yalnız pencere açılınca yüklenir.
 */
export function LocationPicker({ open, onClose, onPick, level = "DD-2", initial }: { open: boolean; onClose: () => void; onPick: (p: PickedLocation) => void; level?: DdLevel; initial?: { lat: number; lon: number } }): JSX.Element | null {
  const [lat, setLat] = useState(initial ? coord(initial.lat) : "");
  const [lon, setLon] = useState(initial ? coord(initial.lon) : "");
  const [vals, setVals] = useState<TdthValues | null>(null);
  const [msg, setMsg] = useState<string>();
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<LMap>();
  const marker = useRef<CircleMarker>();
  const pLat = parseTR(lat), pLon = parseTR(lon);

  // Harita: ilk açılışta kur, tıklanan noktayı alanlara yaz
  useEffect(() => {
    if (!open) return;
    let off = false;
    void (async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (off || !box.current) return;
      const m = L.map(box.current, { attributionControl: true }).setView(initial ? [initial.lat, initial.lon] : [39.0, 35.2], initial ? 13 : 6);
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "© <a href=\"https://www.openstreetmap.org/copyright\" target=\"_blank\" rel=\"noreferrer\">OpenStreetMap</a>" }).addTo(m);
      marker.current = L.circleMarker(initial ? [initial.lat, initial.lon] : [0, 0], { radius: 7, color: "#B42318", weight: 2, fillOpacity: 0.6 });
      if (initial) marker.current.addTo(m);
      m.on("click", (e) => { setLat(coord(e.latlng.lat)); setLon(coord(e.latlng.lng)); });
      map.current = m;
    })();
    return () => { off = true; map.current?.remove(); map.current = undefined; };
  }, [open]);

  // Koordinat değişince işaretçi + ızgara değeri
  useEffect(() => {
    setVals(null); setMsg(undefined);
    if (pLat === null || pLon === null) return;
    if (map.current && marker.current) { marker.current.setLatLng([pLat, pLon]); if (!map.current.hasLayer(marker.current)) marker.current.addTo(map.current); if (!map.current.getBounds().contains([pLat, pLon])) map.current.panTo([pLat, pLon]); }
    let off = false;
    lookupTdth(pLat, pLon, level).then((v) => { if (off) return; setVals(v); if (!v) setMsg(t("Bu nokta AFAD ızgarasının dışında (Türkiye dışı ya da deniz). SS ve S1'i elle girin.")); })
      .catch(() => { if (!off) setMsg(t("Deprem tehlike verisi yüklenemedi; bağlantıyı kontrol edin.")); });
    return () => { off = true; };
  }, [pLat, pLon, level]);

  const locate = (): void => {
    if (!navigator.geolocation) { setMsg(t("Tarayıcı konum bilgisi vermiyor.")); return; }
    navigator.geolocation.getCurrentPosition((p) => { setLat(coord(p.coords.latitude)); setLon(coord(p.coords.longitude)); map.current?.setView([p.coords.latitude, p.coords.longitude], 15); },
      () => setMsg(t("Konum izni verilmedi; haritadan seçin ya da koordinat yazın.")), { enableHighAccuracy: true, timeout: 10000 });
  };

  if (!open) return null;
  return createPortal(
    <Dialog open onClose={onClose} label={t("Konumdan deprem değerleri")} width={720}
      footer={<>
        <a href={AFAD_TDTH_URL} target="_blank" rel="noreferrer" className="mr-auto text-[11px] text-cad-accent underline">{t("AFAD'da teyit et")}</a>
        <button type="button" className="cad-btn-box" onClick={onClose}>{t("Vazgeç")}</button>
        <button type="button" className="cad-btn-primary" disabled={!vals || pLat === null || pLon === null} onClick={() => { if (vals && pLat !== null && pLon !== null) onPick({ lat: pLat, lon: pLon, ...vals }); }}>{t("Uygula")}</button>
      </>}>
      <p className="m-0 text-[11px] text-cad-ink2">{t("Haritada proje yerine tıklayın ya da koordinatı yazın.")}</p>
      <div ref={box} data-testid="konum-harita" className="h-[360px] w-full border border-cad-line bg-[#E8ECEF]" />
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <label className="grid gap-0.5 text-[11px]">{t("Enlem (°)")}<input className="cad-prop-input !h-6 !border !border-solid !border-cad-line !bg-white font-mono" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="39,925533" aria-label={t("Enlem")} /></label>
        <label className="grid gap-0.5 text-[11px]">{t("Boylam (°)")}<input className="cad-prop-input !h-6 !border !border-solid !border-cad-line !bg-white font-mono" value={lon} onChange={(e) => setLon(e.target.value)} placeholder="32,866287" aria-label={t("Boylam")} /></label>
        <button type="button" className="cad-btn-box !h-6" onClick={locate}>📍 {t("Konumumu kullan")}</button>
      </div>
      <div className="grid gap-1 rounded-[3px] border border-cad-line bg-white px-3 py-2" aria-live="polite" aria-label={t("Izgara değerleri")}>
        {vals ? <div className="flex gap-6 font-mono text-[13px]"><span>SS ≈ <b>{fmt(vals.SS, 3)}</b></span><span>S1 ≈ <b>{fmt(vals.S1, 3)}</b></span><span className="text-cad-ink3">{level}</span></div>
          : <span className="text-[12px] text-cad-ink3">{msg ?? (pLat === null || pLon === null ? t("Nokta seçilmedi") : t("Hesaplanıyor…"))}</span>}
        <span className="text-[10px] text-cad-ink3">{t("AFAD TDTH parametre ızgarasından (0,1°) enterpolasyonla, yaklaşık ±%1. Resmî rapor için değerleri AFAD'dan teyit edin; zemin sınıfı zemin etüdünden.")}</span>
      </div>
    </Dialog>, document.body);
}
